/* eslint-disable no-console */
/* eslint-disable import/no-import-module-exports,import/first */
// Imported before the __webpack_public_path__ assignment below so it's defined when that runs; the
// assignment must precede the app imports (their modules may trigger chunk loads), hence the order.
// eslint-disable-next-line import/order
import { resolvePublicPath } from './ThemedAppClientRoot.utils';

// Set webpack/Rspack's runtime public path (the base URL prepended to lazy-chunk filenames) before
// any other module loads. Delegated to resolvePublicPath so the derivation is unit-testable — the
// same bundle is deployed to test and prod, so the base must be resolved at runtime (NOA-3244).
// eslint-disable-next-line camelcase
__webpack_public_path__ = resolvePublicPath();

import React, { StrictMode } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { loadableReady } from '@loadable/component';

import { setApiClientStore } from '@shared/apiClient';

import { store } from './redux/store';
import { createEmotionCache } from './styles/emotionCache';

import '../assets/fonts/questa/questa.scss';
import '../assets/fonts/bangers/bangers.scss';
import '../assets/fonts/poppins/poppins.scss';
import { AppProviders } from './apps/Themed/AppProviders';
import { ThemedAppRoot } from './apps/Themed/ThemedAppRoot';

// Initialize ApiClient with store immediately
setApiClientStore(store);

if (module.hot) {
  // Reducer HMR is handled where the store is created (see redux/createStore.ts, which accepts
  // './index' and calls replaceReducer). A second accept here previously wrapped the replacement in
  // `() => async () => {…}`, so it never ran — and a no-op accept suppresses the full reload, leaving
  // stale reducers in dev. Dropped (NOA-3244); redux edits now fall back to a clean full reload.
  module.hot.addStatusHandler((status) => {
    if (status === 'check' || status === 'prepare') {
      console.clear();
    }
  });
}

// One cache for the app's lifetime; must use the same key as the SSR renderer so hydration
// reuses the server-inserted <style data-emotion="naf …"> tags instead of re-inserting.
const emotionCache = createEmotionCache();

// CLIENT ENTRY: hydrates the SSR markup. Composes the shared `AppProviders` (Emotion → redux →
// Helmet → router) with the client-only `BrowserRouter` strategy and `StrictMode`; the browser-only
// providers (Auth0, tracking, ThemeContext) live inside `ThemedAppRoot`.
const jsx = () => (
  <StrictMode>
    <AppProviders
      emotionCache={emotionCache}
      store={store}
      renderRouter={(children) => <BrowserRouter>{children}</BrowserRouter>}
    >
      <ThemedAppRoot />
    </AppProviders>
  </StrictMode>
);

const hydrate = () => {
  const container = document.getElementById('root');
  if (container == null) throw new Error('Failed to find the root element');
  hydrateRoot(container, jsx());
};

// loadableReady waits for the code-split chunks the server rendered (their <script> tags are in the
// document) to load before hydrating, so React hydrates the exact same tree the server produced with
// no fallback flash (NOA-3244 D7).
//
// React 19 hoists @loadable's `async` entry into <head>, so the entry can run before <body> is parsed.
// loadableReady reads its state script (`__LOADABLE_REQUIRED_CHUNKS__`, emitted at the end of <body>)
// synchronously when called — call it too early and that lookup misses ("loadableReady() requires
// state"), so it hydrates without waiting and the flash / hydration mismatch returns. Gate the *call*
// on DOMContentLoaded (not just hydrate, as before) so the state script — and #root — are parsed first.
const bootstrap = () => loadableReady(hydrate);
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
} else {
  bootstrap();
}
